import PropTypes from 'prop-types';
import { Component } from 'react';
import { FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import './ErrorBoundary.css';

function isChunkLoadError(error) {
  if (!error) {
    return false;
  }

  return /ChunkLoadError|Loading chunk|Loading CSS chunk|Importing a module script failed/i.test(
    `${error.name || ''} ${error.message || ''}`
  );
}

function reloadPage() {
  window.location.reload();
}

/**
 * Garde-fou global : évite l'écran blanc lorsqu'une page chargée à la demande
 * (chunk) ne peut pas être récupérée ou qu'un rendu échoue.
 * Doit rester monté en permanence au-dessus des routes.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);

    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Erreur non gérée interceptée par ErrorBoundary', error, info);
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;

    if (!error) {
      return children;
    }

    const chunkError = isChunkLoadError(error);

    return (
      <div className="error-boundary" role="alert">
        <section className="panel glass-panel error-boundary__panel">
          <span aria-hidden="true" className="error-boundary__icon">
            <FiAlertTriangle size={28} />
          </span>
          <h1 className="error-boundary__title">
            {chunkError ? 'Mise à jour du site en cours' : 'Une erreur est survenue'}
          </h1>
          <p className="error-boundary__description">
            {chunkError
              ? 'Une nouvelle version du site a été publiée pendant votre navigation et un fichier n’a pas pu être chargé. Rechargez la page pour continuer.'
              : 'Cette page n’a pas pu s’afficher correctement. Rechargez la page pour réessayer.'}
          </p>
          <div className="error-boundary__actions">
            <button className="error-boundary__button" type="button" onClick={reloadPage}>
              <FiRefreshCw aria-hidden="true" size={16} />
              <span>Recharger la page</span>
            </button>
            <a className="error-boundary__link" href="/">
              Retour à l’accueil
            </a>
          </div>
          {process.env.NODE_ENV !== 'production' && <pre className="error-boundary__details">{error.message}</pre>}
        </section>
      </div>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node
};

ErrorBoundary.defaultProps = {
  children: null
};

export default ErrorBoundary;
